import React, { useEffect, useState } from 'react';
import AppLayout from '../../Layouts/AppLayout';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { useUsuarios } from '../../../hooks/useUsuarios';
import { useUserManagement } from './hooks/useUserManagement';
import { useQuery } from '@tanstack/react-query';
import { rolesPermissionsService } from '@/services/rolesPermissionsService';
import {
  UserManagementTable,
  SearchBar,
  Pagination,
  PaginationInfo,
  UserRoleDialog,
  PasswordDialog,
  ViewPasswordDialog,
  CreateUserDialog,
  UserProfileDialog,
} from './components';
import { Usuario } from '@/auth/types';
import { UserPasswordViewData } from './types';
import { useAuthorization } from '@/auth/hooks/useAuthorization';


const UserManagementComponent: React.FC = () => {
  // Fetch roles from backend
  const { data: roles = [], isLoading: rolesLoading } = useQuery({
    queryKey: ['roles'],
    queryFn: rolesPermissionsService.roles.getAll
  });

  // Transform roles for the dialog component
  const availableRoles = roles
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
    .map(role => ({
      value: role.name,
      label: role.display_name || role.name
    }));

  const {
    users = [],
    handleSearch,
    loading,
    setSearchText,
    searchText,
    currentSearchQuery,
    links,
    currentPage,
    goToPage,
    nextPage,
    prevPage,
    fetchDataUsers,
    totalItems,
    actualPerPage
  } = useUsuarios(true, 10);

  const {
    handleSaveRoles,
    handleChangePassword,
    handleViewPassword,
    handleCopyPassword,
    handleNotifyCredentials,
    handleUpdateProfile,
    handleCreateUser,
    loadingActions,
  } = useUserManagement(fetchDataUsers);
  const { canViewUserPasswords, canManageAuthorization } = useAuthorization();

  // Dialog states
  const [roleDialogState, setRoleDialogState] = useState<{
    open: boolean;
    user: Usuario | null;
  }>({ open: false, user: null });

  const [passwordDialogState, setPasswordDialogState] = useState<{
    open: boolean;
    user: Usuario | null;
  }>({ open: false, user: null });

  const [profileDialogState, setProfileDialogState] = useState<{
    open: boolean;
    user: Usuario | null;
  }>({ open: false, user: null });
  const [viewPasswordDialogState, setViewPasswordDialogState] = useState<{
    open: boolean;
    user: Usuario | null;
    data: UserPasswordViewData | null;
  }>({ open: false, user: null, data: null });

  const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);

  useEffect(() => {
    fetchDataUsers();
  }, []);

  // Dialog handlers
  const handleEditRoles = (user: Usuario) => {
    setRoleDialogState({ open: true, user });
  };

  const handleChangePasswordClick = (user: Usuario) => {
    setPasswordDialogState({ open: true, user });
  };

  const handleEditProfile = (user: Usuario) => {
    setProfileDialogState({ open: true, user });
  };

  const handleViewPasswordClick = async (user: Usuario) => {
    setViewPasswordDialogState({ open: true, user, data: null });

    try {
      const data = await handleViewPassword(user);
      setViewPasswordDialogState({ open: true, user, data });
    } catch (error) {
      setViewPasswordDialogState({ open: false, user: null, data: null });
    }
  };

  const handleNotifyCredentialsClick = (user: Usuario) => {
    handleNotifyCredentials(user);
  };

  const handleCreateUserClick = () => {
    setCreateUserDialogOpen(true);
  };

  // Dialog close handlers
  const closeRoleDialog = () => {
    setRoleDialogState({ open: false, user: null });
  };

  const closePasswordDialog = () => {
    setPasswordDialogState({ open: false, user: null });
  };

  const closeProfileDialog = () => {
    setProfileDialogState({ open: false, user: null });
  };

  const closeViewPasswordDialog = () => {
    setViewPasswordDialogState({ open: false, user: null, data: null });
  };

  const closeCreateUserDialog = () => {
    setCreateUserDialogOpen(false);
  };

  return (
    <AppLayout title="Gestión de Usuarios">
      <div className="bg-white p-2 sm:p-4 max-w-7xl mx-auto shadow-md rounded-md">
        {(loading || rolesLoading) && <Progress value={undefined} className="h-1" />}

        <div className="bg-white rounded-md">
          <div
            className={`px-2 py-2 flex flex-row justify-between items-center flex-wrap gap-4 ${loading ? 'hidden' : 'flex'
              }`}
          >
            <div className="flex gap-2 items-center">
              {currentSearchQuery && (
                <span className="text-sm text-gray-500">
                  Búsqueda: "{currentSearchQuery}"
                </span>
              )}
            </div>

            <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:flex-nowrap">
              <Button
                onClick={handleCreateUserClick}
                size="sm"
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Nuevo Usuario
              </Button>

              <div className="flex min-w-0 flex-1 basis-full sm:basis-auto sm:flex-none">
                <SearchBar
                  searchText={searchText}
                  onSearchChange={setSearchText}
                  onSearch={handleSearch}
                  loading={loading}
                  placeholder="Buscar usuario..."
                />
              </div>
            </div>
          </div>
        </div>

        <UserManagementTable
          users={users}
          onEditRoles={handleEditRoles}
          onEditProfile={handleEditProfile}
          onChangePassword={handleChangePasswordClick}
          onViewPassword={handleViewPasswordClick}
          onNotifyCredentials={handleNotifyCredentialsClick}
          loadingActions={loadingActions}
          canViewPasswords={canViewUserPasswords}
          canManageRoles={canManageAuthorization}
        />

        <PaginationInfo
          currentPage={currentPage}
          perPage={actualPerPage}
          total={totalItems}
          loading={loading}
        />

        <Pagination
          currentPage={currentPage}
          links={links}
          onPageChange={goToPage}
          onNextPage={nextPage}
          onPrevPage={prevPage}
        />
      </div>

      {/* Dialogs */}
      <UserRoleDialog
        user={roleDialogState.user}
        open={roleDialogState.open}
        onClose={closeRoleDialog}
        onSave={handleSaveRoles}
        availableRoles={availableRoles}
      />

      <PasswordDialog
        user={passwordDialogState.user}
        open={passwordDialogState.open}
        onClose={closePasswordDialog}
        onSave={handleChangePassword}
      />

      <CreateUserDialog
        open={createUserDialogOpen}
        onClose={closeCreateUserDialog}
        onSave={handleCreateUser}
        availableRoles={availableRoles}
      />

      <ViewPasswordDialog
        user={viewPasswordDialogState.user}
        open={viewPasswordDialogState.open}
        onClose={closeViewPasswordDialog}
        passwordData={viewPasswordDialogState.data}
        loading={
          Boolean(viewPasswordDialogState.user)
          && loadingActions[`view-password-${viewPasswordDialogState.user.id}`]
        }
        onCopy={handleCopyPassword}
      />

      <UserProfileDialog
        user={profileDialogState.user}
        open={profileDialogState.open}
        onClose={closeProfileDialog}
        onSave={handleUpdateProfile}
      />
    </AppLayout>
  );
};

export default UserManagementComponent;
